import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { dateTimeLocalToISO, formatDateTimeForInput } from '../../utils/timezone';
import { formatAddress } from '../../utils/address';
import {
  type NurseryCustomerAddress,
  type NurseryDeliveryZone,
  type NurseryDespatchMethod,
  type OrderDespatchPayload,
} from '../../types/nursery';

/**
 * How, when and where an order goes — shared by the new-order form and the
 * order page's Despatch card. Controlled; the parent holds the value.
 *
 * `delivery_zone_id` left undefined means "work it out from the address",
 * which the server does the same way the website quotes a delivery fee. The
 * zone is also the order's region when loads are planned, so setting it by
 * hand is for the order whose postcode lands it in the wrong truck run.
 */

export interface OrderDespatchFieldsProps {
  value: OrderDespatchPayload;
  onChange: (value: OrderDespatchPayload) => void;
  /** The customer's delivery addresses. */
  addresses: NurseryCustomerAddress[];
  zones: NurseryDeliveryZone[];
  timezone: string;
  /** Link target for adding an address, when the customer is known. */
  customerId?: number | null;
  /** Shown beside "work it out" — the zone the order has now. */
  currentZoneName?: string | null;
  /**
   * Whether the customer's orders go through a retailer's cross dock. Only
   * then is Cross dock offered (it stays on an order already sent by it).
   * Undefined, from a server older than the setting: always offered.
   */
  usesCrossDock?: boolean;
}

const METHODS: NurseryDespatchMethod[] = ['run', 'carrier', 'post', 'collection', 'cross_dock'];

const inputCls =
  'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export function OrderDespatchFields({
  value, onChange, addresses, zones, timezone, customerId, currentZoneName, usesCrossDock,
}: OrderDespatchFieldsProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderDespatchFields' });
  const method = value.despatch_method ?? 'collection';
  const set = (patch: Partial<OrderDespatchPayload>) => onChange({ ...value, ...patch });
  const deliveryAddresses = addresses.filter((a) => a.address_type === 'delivery' && a.id);
  const methods = METHODS.filter((m) => m !== 'cross_dock' || usesCrossDock !== false || method === 'cross_dock');

  return (
    <div className="space-y-4">
      <div>
        <span className="block text-sm font-medium text-neutral-700 mb-1">{t('howItGoes')}</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('methodAria')}>
          {methods.map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={method === m}
              onClick={() => {
                // Leaving collection for a delivery: start from the customer's
                // default address rather than none.
                const fallback = m !== 'collection' && !value.delivery_address_id
                  ? deliveryAddresses.find((a) => a.is_default)?.id ?? deliveryAddresses[0]?.id
                  : undefined;
                set(fallback ? { despatch_method: m, delivery_address_id: fallback } : { despatch_method: m });
              }}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-sm',
                method === m
                  ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium'
                  : 'bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50',
              )}
            >
              {t(`method.${m}`)}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm text-neutral-700">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={value.priority === 'asap'}
          onChange={(e) => set({ priority: e.target.checked ? 'asap' : 'normal' })}
        />
        <span>
          {t('asap')}
          <span className="block text-xs text-neutral-500">{t('asapHint')}</span>
        </span>
      </label>

      {method === 'collection' ? (
        <div>
          <label htmlFor="despatch-collection-at" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('collectionTime')}
          </label>
          <input
            id="despatch-collection-at"
            type="datetime-local"
            value={value.collection_at ? formatDateTimeForInput(value.collection_at, timezone) : ''}
            onChange={(e) => set({ collection_at: e.target.value ? dateTimeLocalToISO(e.target.value, timezone) : null })}
            className={inputCls}
          />
          <p className="text-xs text-neutral-500 mt-1">{t('collectionHint')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="despatch-address" className="block text-sm font-medium text-neutral-700 mb-1">
              {t('deliveryAddress')}
            </label>
            {deliveryAddresses.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {customerId ? (
                  <Trans
                    t={t}
                    i18nKey="noAddress"
                    components={{ link: <Link to={`/accounts/customers/${customerId}/edit`} className="underline" /> }}
                  />
                ) : (
                  t('chooseCustomer')
                )}
              </p>
            ) : (
              <select
                id="despatch-address"
                value={value.delivery_address_id ?? ''}
                onChange={(e) => set({ delivery_address_id: e.target.value ? Number(e.target.value) : null })}
                className={inputCls}
              >
                <option value="">{t('noAddressOption')}</option>
                {deliveryAddresses.map((a) => (
                  <option key={a.id} value={a.id}>
                    {[a.label, formatAddress(a)].filter(Boolean).join(' — ')}
                    {a.is_default ? t('defaultSuffix') : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label htmlFor="despatch-zone" className="block text-sm font-medium text-neutral-700 mb-1">
              {t('deliveryZone')}
            </label>
            <select
              id="despatch-zone"
              value={value.delivery_zone_id ?? ''}
              onChange={(e) => set({ delivery_zone_id: e.target.value ? Number(e.target.value) : undefined })}
              className={inputCls}
            >
              <option value="">
                {currentZoneName ? t('automaticNow', { zone: currentZoneName }) : t('automatic')}
              </option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">{t('zoneHint')}</p>
          </div>
        </div>
      )}

      <div>
        <span className="block text-sm font-medium text-neutral-700 mb-1">{t('despatchWindow')}</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="despatch-from" className="block text-xs text-neutral-500 mb-1">{t('notBefore')}</label>
            <input
              id="despatch-from"
              type="date"
              value={value.despatch_from ?? ''}
              onChange={(e) => set({ despatch_from: e.target.value || null })}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="despatch-by" className="block text-xs text-neutral-500 mb-1">{t('mustGoBy')}</label>
            <input
              id="despatch-by"
              type="date"
              value={value.despatch_by ?? ''}
              min={value.despatch_from ?? undefined}
              onChange={(e) => set({ despatch_by: e.target.value || null })}
              className={inputCls}
            />
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          {t('windowHint')}
        </p>
      </div>
    </div>
  );
}

export default OrderDespatchFields;
